import { Link } from "react-router-dom";
import { BadgeCheck, Bookmark, Clock, MapPin, Share2, Sparkles, TrendingUp } from "lucide-react";
import { compactNumber } from "@/lib/powerApi";

/**
 * PetitionCard — the workhorse card used across Home, Explore, Profile.
 *
 * Props:
 *  - petition: hydrated petition object from /api/power/petitions/*
 *  - onShare?: (petition) => void
 *  - onBookmark?: (petition) => void
 *  - bookmarked?: boolean
 *  - compact?: boolean            // small variant used in horizontal rails
 */
export default function PetitionCard({ petition: p, onShare, onBookmark, bookmarked, compact }) {
  if (!p) return null;
  return (
    <article data-testid={`power-card-${p.slug}`}
      className={[
        "group bg-white rounded-2xl border border-[#E4EAF3] overflow-hidden shadow-sm hover:shadow-xl transition-shadow",
        compact ? "min-w-[260px] max-w-[260px] flex-shrink-0" : "w-full",
      ].join(" ")}>
      <Link to={`/power/p/${p.slug}`} className="block">
        <div className="relative aspect-[16/9] bg-gradient-to-br from-[#1E88FF]/20 to-[#0A1A2F]/30">
          {p.cover_url ? (
            <img src={p.cover_url} alt="" loading="lazy"
              className="w-full h-full object-cover" />
          ) : null}
          <div className="absolute top-2 left-2 flex gap-1.5">
            {p.is_featured && (
              <span data-testid={`badge-featured-${p.slug}`}
                className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide bg-[#1E88FF] text-white px-2 py-1 rounded-full shadow">
                <Sparkles className="w-3 h-3" /> Featured
              </span>
            )}
            {p.is_urgent && (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide bg-[#FF3B30] text-white px-2 py-1 rounded-full shadow">
                Urgent
              </span>
            )}
            {p.is_completed && (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide bg-[#22C55E] text-white px-2 py-1 rounded-full shadow">
                Victory
              </span>
            )}
          </div>
          <div className="absolute top-2 right-2 flex gap-1">
            <button
              data-testid={`card-bookmark-${p.slug}`}
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); onBookmark?.(p); }}
              className={"w-8 h-8 rounded-full bg-white/90 backdrop-blur flex items-center justify-center shadow hover:scale-110 transition-transform " + (bookmarked ? "text-[#1E88FF]" : "text-[#0A1A2F]/70")}
              aria-label="Bookmark">
              <Bookmark className="w-4 h-4" fill={bookmarked ? "currentColor" : "none"} />
            </button>
            <button
              data-testid={`card-share-${p.slug}`}
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); onShare?.(p); }}
              className="w-8 h-8 rounded-full bg-white/90 backdrop-blur flex items-center justify-center shadow hover:scale-110 transition-transform text-[#0A1A2F]/70"
              aria-label="Share">
              <Share2 className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className={compact ? "p-3" : "p-4"}>
          <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-[#1E88FF] mb-1.5">
            <span>{p.category}</span>
            {p.location ? <>
              <span className="text-[#0A1A2F]/20">•</span>
              <span className="text-[#0A1A2F]/50 inline-flex items-center gap-0.5"><MapPin className="w-3 h-3" /> {p.location}</span>
            </> : null}
          </div>
          <h3 className={"font-bold text-[#0A1A2F] leading-snug " + (compact ? "text-sm line-clamp-2" : "text-lg line-clamp-2")}>
            {p.title}
          </h3>
          <div className="flex items-center gap-1.5 mt-2 text-[11px] text-[#0A1A2F]/60">
            <div className="w-4 h-4 rounded-full flex-shrink-0"
              style={{ background: p.creator?.avatar_color || "#1E88FF" }} />
            <span className="truncate">{p.creator?.name || "Anonymous"}</span>
            {p.creator?.verified && <BadgeCheck className="w-3 h-3 text-[#1E88FF]" fill="#1E88FF" stroke="white" />}
          </div>

          <ProgressBar count={p.signature_count} goal={p.goal} pct={p.progress_pct} />

          <div className="flex items-center justify-between mt-2 text-[11px] text-[#0A1A2F]/60">
            <div className="inline-flex items-center gap-1 font-semibold text-[#0A1A2F]">
              <TrendingUp className="w-3 h-3 text-[#1E88FF]" />
              {compactNumber(p.signature_count)} of {compactNumber(p.goal)} signed
            </div>
            {p.days_left !== null && p.days_left !== undefined && !p.is_completed && (
              <div className="inline-flex items-center gap-1">
                <Clock className="w-3 h-3" /> {p.days_left}d left
              </div>
            )}
          </div>
        </div>
      </Link>
    </article>
  );
}

export function ProgressBar({ count, goal, pct }) {
  const p = Math.max(0, Math.min(100, Number(pct ?? ((count / (goal || 1)) * 100)) || 0));
  return (
    <div className="mt-3">
      <div className="h-2 rounded-full bg-[#E4EAF3] overflow-hidden">
        <div
          data-testid="power-progress"
          className="h-full rounded-full bg-gradient-to-r from-[#1E88FF] to-[#0A1A2F] transition-all"
          style={{ width: `${p}%` }}
        />
      </div>
    </div>
  );
}
